import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Badge,
  Spinner,
  Collapse,
  Alert
} from "react-bootstrap";
import { 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaTimesCircle,
  FaChevronDown,
  FaChevronUp,
  FaHistory,
  FaBullhorn,
  FaVideo,
  FaImages,
  FaCalendarCheck,
  FaHeadset,
  FaCreditCard,
  FaChartBar,
  FaFacebookMessenger
} from "react-icons/fa";
import { adsStatusService } from "../../services/api";

const AdsStatus = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [expandedServices, setExpandedServices] = useState({});
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [services, setServices] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);
  const [wsConnection, setWsConnection] = useState(null);
  
  // Map icon names from API to React Icon components
  const iconMap = {
    bullhorn: FaBullhorn,
    video: FaVideo,
    images: FaImages,
    'calendar-check': FaCalendarCheck,
    headset: FaHeadset,
    'credit-card': FaCreditCard,
    'chart-bar': FaChartBar,
    'facebook-messenger': FaFacebookMessenger
  };

  useEffect(() => {
    const loadStatusData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch services and incidents in parallel
        const [servicesResponse, incidentsResponse] = await Promise.all([
          adsStatusService.getServices(),
          adsStatusService.getIncidents({ limit: 10, days: 30 })
        ]);
        
        if (servicesResponse.success && servicesResponse.data.services) {
          // Map services with icon components
          const mappedServices = servicesResponse.data.services.map(service => ({
            ...service,
            icon: iconMap[service.icon] || FaBullhorn,
            name: service.nameTh || service.name,
            description: service.descriptionTh || service.description,
            subServices: service.subServices?.map(sub => ({
              ...sub,
              name: sub.nameTh || sub.name
            })) || []
          }));
          setServices(mappedServices);
        }
        
        if (incidentsResponse.success && incidentsResponse.data.incidents) {
          // Map incidents with formatted data
          const mappedIncidents = incidentsResponse.data.incidents.map(incident => ({
            ...incident,
            title: incident.titleTh || incident.title,
            description: incident.descriptionTh || incident.description,
            updates: incident.updates?.map(update => ({
              time: new Date(update.timestamp).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }),
              message: update.messageTh || update.message
            })) || []
          }));
          setIncidents(mappedIncidents);
        }
        
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to load status data:', error);
        setError('ไม่สามารถโหลดข้อมูลสถานะได้ กรุณาลองใหม่อีกครั้ง');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadStatusData();
    
    // Set up WebSocket for real-time updates
    try {
      const ws = adsStatusService.subscribeToUpdates(
        (data) => {
          // Handle real-time status updates
          if (data.type === 'status_update') {
            setServices(prevServices => 
              prevServices.map(service => 
                service.id === data.data.serviceId 
                  ? { ...service, status: data.data.status }
                  : service
              )
            );
            setLastUpdated(new Date());
          } else if (data.type === 'incident_created' || data.type === 'incident_updated') {
            // Reload incidents when new incident is created or updated
            adsStatusService.getIncidents({ limit: 10, days: 30 })
              .then(response => {
                if (response.success && response.data.incidents) {
                  const mappedIncidents = response.data.incidents.map(incident => ({
                    ...incident,
                    title: incident.titleTh || incident.title,
                    description: incident.descriptionTh || incident.description,
                    updates: incident.updates?.map(update => ({
                      time: new Date(update.timestamp).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }),
                      message: update.messageTh || update.message
                    })) || []
                  }));
                  setIncidents(mappedIncidents);
                }
              });
          }
        },
        (error) => {
          console.error('WebSocket error:', error);
        }
      );
      setWsConnection(ws);
      
      // Cleanup WebSocket on unmount
      return () => {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    } catch (error) {
      console.error('Failed to establish WebSocket connection:', error);
    }
  }, []);

  // Toggle service expansion
  const toggleService = (serviceId) => {
    setExpandedServices(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Get overall status
  const getOverallStatus = () => {
    if (services.length === 0) return "operational";
    
    const hasOutage = services.some(s => s.status === "outage");
    const hasDegraded = services.some(s => s.status === "degraded");
    
    if (hasOutage) return "outage";
    if (hasDegraded) return "degraded";
    return "operational";
  };

  // Render status icon
  const renderStatusIcon = (status, size = 20) => {
    switch (status) {
      case "operational":
        return <FaCheckCircle className="text-success" size={size} />;
      case "degraded":
        return <FaExclamationTriangle className="text-warning" size={size} />;
      case "outage":
        return <FaTimesCircle className="text-danger" size={size} />;
      default:
        return <FaCheckCircle className="text-success" size={size} />;
    }
  };

  // Render status text
  const renderStatusText = (status) => {
    switch (status) {
      case "operational":
        return <span className="text-success">ปกติ</span>;
      case "degraded":
        return <span className="text-warning">มีปัญหาบางส่วน</span>;
      case "outage":
        return <span className="text-danger">ไม่สามารถใช้งานได้</span>;
      default:
        return <span className="text-success">ปกติ</span>;
    }
  };

  // Render incident status badge
  const renderIncidentBadge = (status) => {
    switch (status) {
      case "investigating":
        return <Badge bg="warning">กำลังตรวจสอบ</Badge>;
      case "identified":
        return <Badge bg="info">พบสาเหตุแล้ว</Badge>;
      case "monitoring":
        return <Badge bg="primary">กำลังติดตาม</Badge>;
      case "resolved":
        return <Badge bg="success">แก้ไขแล้ว</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  // Refresh status data
  const refreshStatus = async () => {
    try {
      const [servicesResponse, incidentsResponse] = await Promise.all([
        adsStatusService.getServices(),
        adsStatusService.getIncidents({ limit: 10, days: 30 })
      ]);
      
      if (servicesResponse.success && servicesResponse.data.services) {
        const mappedServices = servicesResponse.data.services.map(service => ({
          ...service,
          icon: iconMap[service.icon] || FaBullhorn,
          name: service.nameTh || service.name,
          description: service.descriptionTh || service.description,
          subServices: service.subServices?.map(sub => ({
            ...sub,
            name: sub.nameTh || sub.name
          })) || []
        }));
        setServices(mappedServices);
      }
      
      if (incidentsResponse.success && incidentsResponse.data.incidents) {
        const mappedIncidents = incidentsResponse.data.incidents.map(incident => ({
          ...incident,
          title: incident.titleTh || incident.title,
          description: incident.descriptionTh || incident.description,
          updates: incident.updates?.map(update => ({
            time: new Date(update.timestamp).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }),
            message: update.messageTh || update.message
          })) || []
        }));
        setIncidents(mappedIncidents);
      }
      
      setLastUpdated(new Date());
      setError(null);
    } catch (error) {
      console.error('Failed to refresh status:', error);
      setError('ไม่สามารถรีเฟรชข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  // Format time
  const formatLastUpdated = (date) => {
    return date.toLocaleTimeString('th-TH', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  if (isLoading) {
    return (
      <div className="ads-status-page d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">กำลังโหลดสถานะบริการ...</p>
        </div>
      </div>
    );
  }

  const overallStatus = getOverallStatus();

  return (
    <div className="ads-status-page">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h1 className="mb-2">สถานะบริการ Ads Boost</h1>
          <p className="text-muted mb-0">
            ตรวจสอบสถานะการให้บริการของระบบ Ads Boost
          </p>
        </Col>
      </Row>
      
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Overall Status Banner */}
      <Card 
        className="mb-4 border-0" 
        style={{ 
          background: overallStatus === "operational" 
            ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)" 
            : overallStatus === "degraded"
            ? "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)"
            : "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
        }}
      >
        <Card.Body className="py-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              {overallStatus === "operational" ? (
                <FaCheckCircle className="text-white me-3" size={40} />
              ) : overallStatus === "degraded" ? (
                <FaExclamationTriangle className="text-white me-3" size={40} />
              ) : (
                <FaTimesCircle className="text-white me-3" size={40} />
              )}
              <div>
                <h3 className="text-white mb-1">
                  {overallStatus === "operational" 
                    ? "ระบบทั้งหมดทำงานปกติ" 
                    : overallStatus === "degraded"
                    ? "บางบริการมีปัญหา"
                    : "ระบบมีปัญหา"}
                </h3>
                <p className="text-white mb-0 opacity-75">
                  อัปเดตล่าสุด: {formatLastUpdated(lastUpdated)}
                </p>
              </div>
            </div>
            <div className="text-white text-end d-none d-md-block">
              <small>บริการทั้งหมด: {services.length} รายการ</small>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Services Status List */}
      <Card className="mb-4">
        <Card.Header className="bg-white py-3">
          <h5 className="mb-0">สถานะบริการทั้งหมด</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isExpanded = expandedServices[service.id];
            
            return (
              <div key={service.id}>
                <div 
                  className={`d-flex align-items-center justify-content-between p-3 ${index !== services.length - 1 ? 'border-bottom' : ''}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggleService(service.id)}
                >
                  <div className="d-flex align-items-center">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ 
                        width: "45px", 
                        height: "45px", 
                        backgroundColor: service.status === "operational" ? "#e8f5e9" : service.status === "degraded" ? "#fff3e0" : "#ffebee"
                      }}
                    >
                      <IconComponent 
                        size={20} 
                        className={service.status === "operational" ? "text-success" : service.status === "degraded" ? "text-warning" : "text-danger"}
                      />
                    </div>
                    <div>
                      <h6 className="mb-0">{service.name}</h6>
                      <small className="text-muted">{service.description}</small>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="text-end me-3">
                      {renderStatusIcon(service.status)}
                      <span className="ms-2 d-none d-sm-inline">{renderStatusText(service.status)}</span>
                    </div>
                    {isExpanded ? <FaChevronUp className="text-muted" /> : <FaChevronDown className="text-muted" />}
                  </div>
                </div>
                
                <Collapse in={isExpanded}>
                  <div>
                    <div className="bg-light px-4 py-3">
                      {service.subServices.map((subService, subIndex) => (
                        <div 
                          key={subIndex} 
                          className={`d-flex align-items-center justify-content-between py-2 ${subIndex !== service.subServices.length - 1 ? 'border-bottom' : ''}`}
                        >
                          <span className="text-muted">{subService.name}</span>
                          <div className="d-flex align-items-center">
                            {renderStatusIcon(subService.status, 16)}
                            <span className="ms-2 small">{renderStatusText(subService.status)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Collapse>
              </div>
            );
          })}
        </Card.Body>
      </Card>

      {/* Recent Incidents */}
      <Card>
        <Card.Header className="bg-white py-3 d-flex align-items-center">
          <FaHistory className="me-2 text-muted" />
          <h5 className="mb-0">เหตุการณ์ล่าสุด</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {incidents.length === 0 ? (
            <div className="text-center py-5">
              <FaCheckCircle className="text-success mb-3" size={40} />
              <p className="text-muted mb-0">ไม่มีเหตุการณ์ในช่วง 30 วันที่ผ่านมา</p>
            </div>
          ) : (
            incidents.map((incident, index) => (
              <div 
                key={incident.id} 
                className={`p-4 ${index !== incidents.length - 1 ? 'border-bottom' : ''}`}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h6 className="mb-1">{incident.title}</h6>
                    <small className="text-muted">{formatDate(incident.date)}</small>
                  </div>
                  {renderIncidentBadge(incident.status)}
                </div>
                <p className="text-muted mb-3">{incident.description}</p>
                
                <div className="ps-3 border-start border-2" style={{ borderColor: incident.status === "resolved" ? "#28a745" : "#ffc107" }}>
                  {incident.updates.map((update, updateIndex) => (
                    <div key={updateIndex} className="mb-2">
                      <small className="text-muted fw-bold">{update.time}</small>
                      <p className="mb-0 small">{update.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      {/* Footer Info */}
      <div className="text-center mt-4 mb-3">
        <small className="text-muted">
          หากพบปัญหาในการใช้งาน กรุณาติดต่อทีมซัพพอร์ตผ่านระบบแชท หรือโทร 02-XXX-XXXX
        </small>
      </div>
    </div>
  );
};

export default AdsStatus;
